/**
 * ageBandsFlag — the ONE cross-app read/write path for `lwa_age_bands_v1`, the
 * parent-enabled age-band experience mode (docs/age-band-experience.md): five
 * age-appropriate home shells driven by the shared experience model.
 *
 * Same scheme as extrasFlag.ts (which see for the full rationale): the parent
 * portal (web-app) toggles this flag but CANNOT import web-child's flagGuard
 * writer, so the reader AND a parent-gated writer live here, over the shared
 * `createSignedFlag` primitive (../signedFlag.ts) that replicates the flagGuard
 * envelope byte-for-byte. `lwa_age_bands_v1` is registered in
 * web-child/flagGuard.ts GUARDED_FLAGS too (NOT in DEFAULT_OFF_FLAGS — default
 * ON), so `readGuardedFlag` and the ops hook interoperate.
 *
 * ENABLED 2026-08-30 (owner-directed, testers waiting — the #578 one-path
 * precedent): DEFAULT ON — an absent key reads true, so the age-tuned shells ARE
 * the Grade-Path experience on device with no toggle hunt. A parent-signed
 * `{ v:'off' }` envelope disables per-device (the Settings toggle); a bare/forged
 * localStorage write is IGNORED either way, so a child can neither enable nor
 * disable it. Fleet rollback = revert this default + redeploy (one commit).
 * Kept in lockstep with web-child/flagGuard.ts (NOT in DEFAULT_OFF_FLAGS).
 * Never throws.
 */

import { createSignedFlag } from '../signedFlag';

export const AGE_BANDS_FLAG = 'lwa_age_bands_v1';

const flag = createSignedFlag(AGE_BANDS_FLAG, { defaultOn: true }); // DEFAULT ON (owner-enabled 2026-08-30)

/**
 * True when a PARENT is signed in on this device: a structurally valid,
 * unexpired `auth_token` JWT (the parent-portal session). The child token
 * (`child_auth_token`) does NOT qualify.
 */
export function hasAgeBandsParentGate(): boolean {
  return flag.hasParentGate();
}

/**
 * Read the age-band experience flag. DEFAULT ON. Only a correctly parent-signed
 * envelope can override the default (`{ v:'off', sig }` disables per-device);
 * every other shape — absent, bare 'on'/'off'/'1'/'true', malformed JSON,
 * wrong/missing signature — reads the default TRUE. Never throws. (Same
 * semantics as programOnboarding/flags.ts isProgramFlagOn for DEFAULT_ON keys.)
 */
export function isAgeBandsEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write. Turning it ON (back to the default) clears the key;
 * turning it OFF stores a signed `{ v:'off', sig }` envelope (the per-device
 * disable). Refuses (returns false, writes nothing) without a live parent
 * session — a child can never flip it either way.
 */
export function setAgeBandsEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check, so unit tests
 *  can exercise the enabled paths (mirrors extrasFlag's __setExtrasOnForTests). */
export function __setAgeBandsOnForTests(): void {
  flag.__setOnForTests();
}

/** TEST-ONLY seam: sign the flag OFF (the per-device disable) without the
 *  parent-JWT check — legacy-home suites opt out of the default-ON shells. */
export function __setAgeBandsOffForTests(): void {
  flag.__setOffForTests();
}
